import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getArticleById, getRelatedArticles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import TableOfContents from "./components/TableOfContents";
import ShareBar from "./components/ShareBar";

export default function Detail() {
  const { id = "" } = useParams();
  const article = getArticleById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [favorited, setFavorited] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    setFavorited(isFavorite(id));
  }, [id, isFavorite]);

  useDocumentMeta({
    title: article ? `${article.title} - 黑丝时尚搭配与潮流资讯` : "内容未找到 - 黑丝时尚搭配与潮流资讯",
    description: article ? article.excerpt : "抱歉，未找到对应的内容，请返回首页浏览更多搭配指南。",
    keywords: article ? `${article.tags.join(",")},黑丝,时尚穿搭` : "黑丝,时尚穿搭",
  });

  useEffect(() => {
    if (preview) {
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") setPreview(null);
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
    return undefined;
  }, [preview]);

  if (!article) {
    return (
      <div className="mx-auto flex max-w-[1200px] flex-col items-center px-4 pb-20 pt-32 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
          <i className="ri-file-unknow-line" />
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容未找到</h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          这条内容可能已被移动或删除，返回分类列表继续探索更多黑丝搭配指南吧。
        </p>
        <Link
          to="/categories"
          className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-foreground-950 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          返回分类列表
        </Link>
      </div>
    );
  }

  const related = getRelatedArticles(article, 4);
  const categoryName = categories.find((c) => c.slug === article.category)?.name || "搭配指南";

  const handleFavorite = () => {
    toggleFavorite(article.id);
    setFavorited((v) => !v);
  };

  return (
    <>
      <article className="mx-auto max-w-[1200px] px-4 pb-16 pt-24 md:px-8 md:pt-28">
        <Breadcrumb
          items={[
            { label: "分类列表", to: "/categories" },
            { label: categoryName, to: `/categories?cat=${article.category}` },
            { label: article.title },
          ]}
        />

        <header className="mx-auto mt-6 max-w-3xl text-center">
          <Link
            to={`/categories?cat=${article.category}`}
            className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3.5 py-1.5 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-200"
          >
            <i className="ri-price-tag-3-line" />
            {categoryName}
          </Link>
          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{article.excerpt}</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line" /> {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" /> {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" /> {article.readTime}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" /> {article.popularity}
            </span>
          </div>
        </header>

        <figure className="mt-8 overflow-hidden rounded-lg">
          <button
            type="button"
            onClick={() => setPreview(article.cover)}
            className="block w-full cursor-zoom-in"
            aria-label="查看大图"
          >
            <img
              src={article.cover}
              alt={`${article.title} 黑丝穿搭示范大图`}
              title={`${article.title} - 黑丝时尚搭配`}
              className="max-h-[560px] w-full object-cover object-top"
            />
          </button>
        </figure>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-4">
            <TableOfContents sections={article.sections} />
          </div>

          <div className="lg:col-span-8">
            <div className="flex flex-col gap-10">
              {article.sections.map((section, i) => (
                <section key={section.id} id={section.id} className="scroll-mt-28">
                  <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    <span className="text-sm text-primary-500">0{i + 1}</span>
                    {section.heading}
                  </h2>
                  <div className="mt-4 flex flex-col gap-4">
                    {section.paragraphs.map((p, pi) => (
                      <p key={`${section.id}-p-${pi}`} className="text-base leading-loose text-foreground-700">
                        {p}
                      </p>
                    ))}
                  </div>
                  {section.image && (
                    <figure className="mt-5 overflow-hidden rounded-lg">
                      <button
                        type="button"
                        onClick={() => setPreview(section.image as string)}
                        className="block w-full cursor-zoom-in"
                        aria-label="查看大图"
                      >
                        <img
                          src={section.image}
                          alt={`${article.title} - ${section.heading}`}
                          title={`${article.title} - 黑丝搭配示范`}
                          className="w-full object-cover object-top"
                        />
                      </button>
                    </figure>
                  )}
                </section>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-2">
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-secondary-100 px-3.5 py-1.5 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  # {tag}
                </Link>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-5 border-t border-foreground-200/70 pt-8 sm:flex-row sm:items-center sm:justify-between">
              <ShareBar title={article.title} />
              <button
                type="button"
                onClick={handleFavorite}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-6 py-3 text-sm font-semibold transition-colors cursor-pointer ${
                  favorited
                    ? "bg-primary-500 text-background-50"
                    : "border border-foreground-200 bg-background-50 text-foreground-800 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {favorited ? "已收藏" : "收藏这篇文章"}
              </button>
            </div>
            <p className="mt-3 text-xs text-foreground-400">
              提示：收藏状态仅保存在当前浏览器中，用于演示效果，刷新或更换设备后不会保留。
            </p>
          </div>
        </div>

        <section className="mt-16 border-t border-foreground-200/70 pt-12">
          <div className="mb-7 flex items-end justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">Related</p>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950">相关推荐</h2>
            </div>
            <Link
              to="/categories"
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-foreground-700 transition-colors hover:text-primary-600"
            >
              更多内容 <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
        </section>
      </article>

      {preview && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-foreground-950/90 p-4 md:p-10"
          onClick={() => setPreview(null)}
        >
          <button
            type="button"
            aria-label="关闭大图"
            onClick={() => setPreview(null)}
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-background-50/15 text-2xl text-background-50 transition-colors hover:bg-background-50/25"
          >
            <i className="ri-close-line" />
          </button>
          <img
            src={preview}
            alt="黑丝搭配大图预览"
            className="max-h-full max-w-full rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}